/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Form } from 'react-bootstrap';
import {
    createReview,
    fetchReview,
    updateReview,
    addReviewTicket,
    updateReviewTicket,
    deleteReviewTicket,
    generateRegistrationLink
} from '../../_utilities/reviewUtils';
import { renderAlert } from '../../_utilities/formattingUtils';
import useDefaultsManager from "../../_hooks/useDefaultsManager";
import SelectorInput from "../_ui/formElements/SelectorInput";

const ReviewEditor = () => {
    const navigate = useNavigate();
    const params = useParams();
    const isNew = params.id === 'new';
    const types = useDefaultsManager('type-defaults');

    useEffect(() => {
        const fetchTypes = async () => {
            try {
                await types.fetchDefaults();
            } catch (err) {
                console.error('Failed to fetch type defaults:', err);
            }
        };
        fetchTypes();
    }, []);

    // Validate that route parameter is a positive integer (if not 'new')
    const reviewId = isNew ? null : (() => {
        const id = parseInt(params.id, 10);
        if (!Number.isInteger(id) || id <= 0) {
            throw new Error(`Invalid review ID: must be a positive integer, got ${params.id}`);
        }
        return id;
    })();

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // Review form data
    const [formData, setFormData] = useState({
        prospect_name: '',
        prospect_email: '',
        prospect_company: '',
        prospect_url: '',
        video_url: '',
        internal_notes: '',
        promo_code: '',
        promo_value: '',
        promo_type: 'credit'
    });

    // Tickets data
    const [tickets, setTickets] = useState([]);
    const [newTicket, setNewTicket] = useState({
        type_id: '',
        title: '',
        description: '',
        includes_promo: false
    });

    useEffect(() => {
        if (!isNew && reviewId) {
            loadReview();
        }
    }, [reviewId, isNew]);

    const loadReview = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchReview(reviewId);
            setFormData({
                prospect_name: data.prospect_name || '',
                prospect_email: data.prospect_email || '',
                prospect_company: data.prospect_company || '',
                prospect_url: data.prospect_url || '',
                video_url: data.video_url || '',
                internal_notes: data.internal_notes || '',
                promo_code: data.promo_code || '',
                promo_value: data.promo_value || '',
                promo_type: data.promo_type || 'credit'
            });
            setTickets(data.tickets || []);
        } catch (err) {
            setError('Failed to load review: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');

            // Validate required fields
            if (!formData.prospect_name || !formData.prospect_email || !formData.prospect_company) {
                throw new Error('Please fill in all required fields (name, email, company)');
            }

            // Prepare data with proper types
            const saveData = {
                ...formData,
                promo_value: formData.promo_value ? parseFloat(formData.promo_value) : null
            };

            if (isNew) {
                const result = await createReview(saveData);
                setSuccessMsg('Review created successfully!');
                setTimeout(() => {
                    navigate(`/admins/reviews/${result.review_id}`);
                }, 1000);
            } else {
                await updateReview(reviewId, saveData);
                setSuccessMsg('Review updated successfully!');
                setTimeout(() => setSuccessMsg(''), 3000);
            }
        } catch (err) {
            setError('Failed to save review: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleAddTicket = async () => {
        try {
            if (!newTicket.title || !newTicket.description) {
                setError('Ticket title and description are required');
                return;
            }

            if (isNew) {
                setError('Please save the review first before adding tickets');
                return;
            }

            setError('');
            const ticket = {
                ...newTicket,
                sort_order: tickets.length,
                includes_promo: newTicket.includes_promo ? 1 : 0
            };

            const result = await addReviewTicket(reviewId, ticket);
            setTickets(prev => [...prev, result]);
            setNewTicket({ type_id: '', title: '', description: '', includes_promo: false });
            setSuccessMsg('Ticket added successfully!');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) {
            setError('Failed to add ticket: ' + err.message);
        }
    };

    const handleDeleteTicket = async (ticketId) => {
        console.log('Deleting ticket', ticketId);
        // Validate ticket ID is a positive integer
        const numTicketId = parseInt(ticketId, 10);
        if (!Number.isInteger(numTicketId) || numTicketId <= 0) {
            setError(`Invalid ticket ID: must be a positive integer`);
            return;
        }

        if (!window.confirm('Are you sure you want to delete this ticket?')) {
            return;
        }

        try {
            await deleteReviewTicket(reviewId, numTicketId);
            setTickets(prev => prev.filter(t => {
                const id = parseInt(t.review_ticket_id, 10);
                return id !== numTicketId
            }));
            setSuccessMsg('Ticket deleted successfully!');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) {
            setError('Failed to delete ticket: ' + err.message);
        }
    };

    const handleCopyLink = () => {
        if (isNew) {
            setError('Please save the review first to generate a registration link');
            return;
        }

        const link = generateRegistrationLink(reviewId);
        navigator.clipboard.writeText(link).then(() => {
            setSuccessMsg('Registration link copied to clipboard!');
            setTimeout(() => setSuccessMsg(''), 3000);
        }).catch(err => {
            setError('Failed to copy link: ' + err.message);
        });
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="component-container p-3 m-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>{isNew ? 'Create New Review' : 'Edit Review'}</h2>
                <div>
                    {!isNew && (
                        <Button
                            variant="info"
                            onClick={handleCopyLink}
                            className="me-2"
                        >
                            <i className="bi bi-link-45deg me-2"></i>
                            Copy Registration Link
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        onClick={() => navigate('/admins/reviews')}
                    >
                        Cancel
                    </Button>
                </div>
            </div>

            {error && renderAlert(error, 'danger')}
            {successMsg && renderAlert(successMsg, 'success')}

            {/* Prospect Information */}
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Prospect Information</h5>
                </Card.Header>
                <Card.Body>
                    <Form>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Prospect Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="prospect_name"
                                        value={formData.prospect_name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Prospect Email *</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="prospect_email"
                                        value={formData.prospect_email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Company Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="prospect_company"
                                        value={formData.prospect_company}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Website URL</Form.Label>
                                    <Form.Control
                                        type="url"
                                        name="prospect_url"
                                        value={formData.prospect_url}
                                        onChange={handleInputChange}
                                        placeholder="https://example.com"
                                    />
                                </Form.Group>
                            </div>
                        </div>
                        <Form.Group className="mb-3">
                            <Form.Label>Video URL</Form.Label>
                            <Form.Control
                                type="url"
                                name="video_url"
                                value={formData.video_url}
                                onChange={handleInputChange}
                                placeholder="https://loom.com/share/..."
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Internal Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="internal_notes"
                                value={formData.internal_notes}
                                onChange={handleInputChange}
                                placeholder="Private notes for internal use only"
                            />
                        </Form.Group>
                    </Form>
                </Card.Body>
            </Card>

            {/* Promotional Offer */}
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Promotional Offer</h5>
                </Card.Header>
                <Card.Body>
                    <Form>
                        <div className="row">
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Promo Code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="promo_code"
                                        value={formData.promo_code}
                                        onChange={handleInputChange}
                                        placeholder="REVIEW2024"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Promo Value</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="promo_value"
                                        value={formData.promo_value}
                                        onChange={handleInputChange}
                                        placeholder="100"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Promo Type</Form.Label>
                                    <Form.Select
                                        name="promo_type"
                                        value={formData.promo_type}
                                        onChange={handleInputChange}
                                    >
                                        <option value="credit">Credit ($)</option>
                                        <option value="discount">Discount (%)</option>
                                        <option value="free_ticket">Free Ticket</option>
                                    </Form.Select>
                                </Form.Group>
                            </div>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            {/* Tickets Section - Only show when editing existing review */}
            {!isNew && (
                <Card className="mb-3">
                    <Card.Header>
                        <h5 className="mb-0">Tickets</h5>
                    </Card.Header>
                    <Card.Body>
                        {tickets.length > 0 && (
                            <div className="mb-4">
                                <h6>Existing Tickets</h6>
                                <div className="list-group">
                                    {tickets.map((ticket, index) => (
                                        <div key={ticket.review_ticket_id} className="list-group-item">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-1">
                                                        {index + 1}. {ticket.title}
                                                        {(ticket.includes_promo === 1 || ticket.includes_promo === '1' || ticket.includes_promo === true) && (
                                                            <span className="badge bg-success ms-2">Includes Promo</span>
                                                        )}
                                                    </h6>
                                                    <p className="mb-0 small text-muted">{ticket.description}</p>
                                                </div>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleDeleteTicket(ticket.review_ticket_id)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <h6>Add New Ticket</h6>
                        <Form>
                            <div className={`d-flex`}>
                                <Form.Group className="mb-3 w-25 me-3">
                                    <Form.Label>Ticket Type</Form.Label>
                                    <SelectorInput inputClass={`mb-3`}
                                                   inputName={`type_id`}
                                                   inputValue={newTicket && newTicket.type_id ? newTicket.type_id : ''}
                                                   inputOptions={types?.defaults?.map ? types.defaults.map(item => ({ label: item.name, value: Number(item.id) })): []}
                                                   onChange={(value) => setNewTicket(prev => ({ ...prev, type_id: value }))}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3 flex-grow-1">
                                    <Form.Label>Ticket Title</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newTicket.title}
                                        onChange={(e) => setNewTicket(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="e.g., Fix broken SSL certificate"
                                    />
                                </Form.Group>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={newTicket.description}
                                    onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Detailed description of the issue and recommended fix"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Include promotional offer with this ticket"
                                    checked={Boolean(newTicket.includes_promo)}
                                    onChange={(e) => setNewTicket(prev => ({ ...prev, includes_promo: e.target.checked }))}
                                />
                            </Form.Group>
                            <Button variant="outline-primary" onClick={handleAddTicket}>
                                <i className="bi bi-plus-circle me-2"></i>
                                Add Ticket
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            )}

            {/* Save Button */}
            <div className="d-flex justify-content-end gap-2">
                <Button
                    variant="secondary"
                    onClick={() => navigate('/admins/reviews')}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-save me-2"></i>
                            {isNew ? 'Create Review' : 'Save Changes'}
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
};

export default ReviewEditor;
